/* eslint-disable react-hooks/exhaustive-deps */
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import LinearProgress from "@material-ui/core/LinearProgress";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Event as RbcEvent,
  NavigateAction,
  View,
  momentLocalizer,
} from "react-big-calendar";
import { useBookingEvents } from "../../hooks/useBookingEvents";
import { stringify } from "../../pages/bookings/util";
import BookingSummary from "../../pages/chats/BookingSummary";
import { Booking } from "../../types";
import {
  formatDisplayDateTimeAbbrev,
  nowToAppLocalDateTime,
} from "../../utils/date";
import { StatusColorContext } from "../contexts";
import { getBookingCardStyle } from "../BookingCard/style";
import BookingCardLabel from "../BookingCard/BookingCardLabel";
import BookingStatusLegend from "../BookingStatusLegend";
import "../printChrome.css";
import { getMaxSimultaneousOverlap } from "./overlap";
import "./styles.css";
import CalendarToolbar from "./Toolbar";

const CalendarEventLabel: React.FC<{ event: RbcEvent; view: View }> = ({
  event,
  view,
}) => (
  <BookingCardLabel
    booking={event.resource as Booking}
    start={event.start as Date}
    end={event.end as Date}
    showTime={view === "day" || view === "week"}
  />
);

/** Day view: the body stays full page width; cards keep DAY_CARD_MIN_WIDTH packed left, and the column scrolls sideways when it exceeds the page. */
const DAY_CARD_MIN_WIDTH = 145;
const DAY_CARD_GAP = 0;
/** Right margin of .rbc-events-container — cards resolve % against it. */
const DAY_EVENTS_CONTAINER_MARGIN = 10;

/** Print: A4 landscape printable width at 8mm margins (≈1060px at 96dpi) plus the time-gutter allowance. */
const PRINT_TARGET_WIDTH = 1060;
const PRINT_GUTTER_ALLOWANCE = 80;

/** Fraction the day grid shrinks by to fit the printable page width; 1 when it fits. */
export const computePrintScale = (requiredWidth: number, targetWidth: number) =>
  Math.min(1, targetWidth / requiredWidth);

/** Edit these to change the printed page header text. */
export const PRINT_TITLE = "Booking Calendar";
export const PRINT_DESCRIPTION = "";

/** Prefix for the suggested "Save as PDF" file name (browsers derive it from document.title). */
export const PRINT_FILE_NAME_PREFIX = "MLS Case Booking";

/** Build the suggested PDF file name "…<range> (<View> View)"; illegal file-name characters become "-". */
export const buildPrintFileName = (
  from?: string,
  to?: string,
  view?: View | "kanban" | "kanban-timeline",
): string => {
  const range =
    from && to
      ? from === to
        ? moment(from).format("DD MMM YYYY")
        : `${moment(from).format("DD MMM YYYY")} - ${moment(to).format("DD MMM YYYY")}`
      : "";
  const viewLabels: Record<string, string> = {
    kanban: "Kanban View",
    "kanban-timeline": "Kanban By Time View",
  };
  const viewLabel = view
    ? ` (${viewLabels[view] ?? `${view.charAt(0).toUpperCase()}${view.slice(1)} View`})`
    : "";
  return `${PRINT_FILE_NAME_PREFIX}${range ? ` ${range}` : ""}${viewLabel}`.replace(
    /[/\\:*?"<>|]/g,
    "-",
  );
};

interface Props {}
const BookingCalendar: React.FC<Props> = (props) => {
  const localizer = momentLocalizer(moment);
  const [dateFrom, setDateFrom] = useState<string>();
  const [dateTo, setDateTo] = useState<string>();
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const {
    events: bookingEvents,
    total: totalEvents,
    loading,
  } = useBookingEvents(dateFrom, dateTo);
  const events = bookingEvents as RbcEvent[];
  const [selectedEvent, setSelectedEvent] = useState<RbcEvent>();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { colors } = useContext(StatusColorContext);
  const dayLayoutAlgorithm = "no-overlap";
  const [currentView, setCurrentView] = useState<View>("day");
  type Density = "tight" | "loose";
  const [density, setDensity] = useState<Density>("tight");
  // Asia/Singapore wall clock — printed header runs outside the fenced
  // filter/fetch area so it uses the SGT engine, not the browser clock.
  const [printedAt, setPrintedAt] = useState<string>(() =>
    formatDisplayDateTimeAbbrev(nowToAppLocalDateTime()),
  );

  const maxOverlap = useMemo(
    () => getMaxSimultaneousOverlap(events as { start: Date; end: Date }[]),
    [events],
  );
  const dayColumnMinWidth =
    Math.max(maxOverlap, 1) * (DAY_CARD_MIN_WIDTH + DAY_CARD_GAP) +
    DAY_CARD_GAP +
    DAY_EVENTS_CONTAINER_MARGIN;
  const printScale = computePrintScale(
    dayColumnMinWidth + PRINT_GUTTER_ALLOWANCE,
    PRINT_TARGET_WIDTH,
  );

  const onRangeChange = (range: Date[] | object) => {
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    if (Array.isArray(range)) {
      startDate = new Date(range[0]);
      endDate = new Date(range[range.length - 1]);
    } else if (typeof range === "object") {
      const { start, end } = range as { start: Date; end: Date };
      startDate = new Date(start);
      endDate = new Date(end);
    }

    if (startDate && endDate) {
      setDateFrom(moment(startDate).format("YYYY-MM-DD"));
      setDateTo(moment(endDate).format("YYYY-MM-DD"));
    }
  };

  const onViewChange = (view: View) => {
    setCurrentView(view);
  };

  const handleNavigate = (newDate: Date, _view?: View, _action?: NavigateAction) => {
    setCurrentDate(newDate);
  };

  // Seed the initial day range so the first fetch has bounds before onRangeChange fires.
  useEffect(() => {
    const today = moment();
    setDateFrom(today.format("YYYY-MM-DD"));
    setDateTo(today.format("YYYY-MM-DD"));
  }, []);

  // Mark the body while mounted so the print rules stay inert on other pages;
  // the @page rule is margins-only — a declared size locks Chrome's print
  // dialog paper/orientation/scale controls.
  useEffect(() => {
    const bodyClasses = ["booking-calendar-active", "booking-print-active"];
    const pageStyleId = "booking-calendar-print-page";
    document.body.classList.add(...bodyClasses);

    const pageStyle = document.createElement("style");
    pageStyle.id = pageStyleId;
    pageStyle.textContent = `@media print { @page { margin: 8mm; } }`;
    document.head.appendChild(pageStyle);

    return () => {
      document.body.classList.remove(...bodyClasses);
      document.getElementById(pageStyleId)?.remove();
    };
  }, []);

  // Refresh the printed-at time on every print (Ctrl+P, menu, window.print)
  // Asia/Singapore wall clock — same shape as the initial printedAt.
  useEffect(() => {
    const refreshPrintedAt = () =>
      setPrintedAt(formatDisplayDateTimeAbbrev(nowToAppLocalDateTime()));
    window.addEventListener("beforeprint", refreshPrintedAt);
    return () => window.removeEventListener("beforeprint", refreshPrintedAt);
  }, []);

  // document.title is the suggested "Save as PDF" file name; restored on unmount.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = buildPrintFileName(dateFrom, dateTo, currentView);
    return () => {
      document.title = previousTitle;
    };
  }, [dateFrom, dateTo, currentView]);

  /**
   * Show dialog when event in the calendar is selected
   * @param event
   */
  const handleSelectEvent = (event: RbcEvent) => {
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };
  const handleClose = () => {
    setIsDialogOpen(false);
  };

  /**
   * Go to booking detail
   * @returns
   */
  const handleGoToBooking = () => {
    if (!selectedEvent) {
      return;
    }
    const win = window.open(
      `/#/bookings/${selectedEvent.resource.id}/show`,
      "_blank",
    );
    win?.focus();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={
        {
          gap: 16,
          // consumed by the day grid's print zoom rule (inherited from here)
          "--print-scale": printScale,
        } as React.CSSProperties
      }
    >
      <div className="booking-calendar-print-header">
        <div className="booking-calendar-print-header-left">
          <h1>{PRINT_TITLE}</h1>
          {PRINT_DESCRIPTION ? <p>{PRINT_DESCRIPTION}</p> : null}
          <p>
            {dateFrom && dateTo
              ? dateFrom === dateTo
                ? moment(dateFrom).format("DD MMM YYYY")
                : `${moment(dateFrom).format("DD MMM YYYY")} – ${moment(dateTo).format("DD MMM YYYY")}`
              : ""}
            {" · "}
            {`${currentView.charAt(0).toUpperCase()}${currentView.slice(1)} View`}{" "}
            · Printed: {printedAt}
          </p>
        </div>
        <div className="booking-calendar-print-header-total">
          Total Events: {totalEvents}
        </div>
      </div>

      <Box
        className={`booking-calendar-wrapper booking-calendar-wrapper--${currentView} booking-calendar-wrapper--${density}${
          loading ? " booking-calendar-wrapper--loading" : ""
        }`}
        style={
          {
            height: 740,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            "--day-column-min-width": `${dayColumnMinWidth}px`,
            "--day-card-width": `${DAY_CARD_MIN_WIDTH}px`,
          } as React.CSSProperties
        }
      >
        {loading ? (
          <LinearProgress
            className="booking-calendar-loading-bar"
            aria-label="Loading bookings"
          />
        ) : null}
        <Calendar
          events={events}
          startAccessor="start"
          endAccessor="end"
          localizer={localizer}
          defaultView="day"
          view={currentView}
          date={currentDate}
          views={["month", "week", "day", "agenda"]}
          onNavigate={handleNavigate}
          onRangeChange={onRangeChange}
          dayLayoutAlgorithm={dayLayoutAlgorithm}
          showMultiDayTimes
          tooltipAccessor={(event: any) => stringify((event as any).resource)}
          formats={{
            eventTimeRangeFormat: () => "",
          }}
          components={{
            toolbar: (props) => (
              <CalendarToolbar
                {...props}
                density={density}
                onToggleDensity={setDensity}
                currentView={currentView}
              />
            ),
            event: (props) => (
              <CalendarEventLabel {...props} view={currentView} />
            ),
          }}
          // @ts-ignore – csstype version mismatch between react-big-calendar and @types/react-redux
          eventPropGetter={(event: any) =>
            getBookingCardStyle(event.resource as Booking, colors)
          }
          onView={onViewChange}
          onSelectEvent={(event: RbcEvent) => handleSelectEvent(event)}
        />
      </Box>

      <Box className="booking-calendar-total">
        <h4>
          Total Events: {totalEvents}
          {loading ? " (updating…)" : ""}
        </h4>
      </Box>

      {selectedEvent && (
        <Dialog
          open={isDialogOpen}
          onClose={handleClose}
          fullWidth={true}
          maxWidth={"md"}
        >
          <DialogTitle>{selectedEvent.title}</DialogTitle>
          <DialogContent>
            <BookingSummary id={selectedEvent.resource?.id} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)} color="secondary">
              Close
            </Button>
            <Button onClick={handleGoToBooking} color="primary">
              Go To Booking
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <BookingStatusLegend />
    </Box>
  );
};

export default BookingCalendar;
